import React, { useCallback, forwardRef } from 'react';
import PropTypes from 'prop-types';
import StopOverlayPropagation from '../StopOverlayPropagation/StopOverlayPropagation';
import { dialogClasses as classes } from './dialogClasses';
import MessageDialog from './variants/MessageDialog';
import DefaultDialog from './variants/DefaultDialog';
import { DialogRoot } from './Dialog.style';

const DIALOG_VARIANTS = {
  default: DefaultDialog,
  message: MessageDialog,
  alert: MessageDialog
};

const Dialog = forwardRef(
  (
    {
      DialogHeaderProps = {},
      DialogContentProps,
      DialogActionsProps,
      variant = 'default',
      ConfirmButtonProps,
      CancelButtonProps,
      isOpen,
      handleClose,
      children,
      fullContentWidth = true,
      size = 'xs',
      scroll = 'paper',
      actions,
      footer,
      ...rest
    },
    ref
  ) => {
    const VariantDialog = DIALOG_VARIANTS[variant];
    const handleCloseByEscKey = useCallback(
      (event, reason) => {
        if (reason === 'escapeKeyDown') {
          handleClose();
        }
      },
      [handleClose]
    );

    const isFullScreen = size === 'fullScreen';

    return (
      <StopOverlayPropagation>
        <DialogRoot
          scroll={scroll}
          onClose={handleCloseByEscKey}
          data-qa-id="dialog-root"
          fullScreen={isFullScreen}
          variant={variant}
          size={size}
          fullWidth={fullContentWidth}
          open={isOpen}
          ref={ref}
          {...rest}>
          <VariantDialog
            DialogHeaderProps={DialogHeaderProps}
            DialogContentProps={DialogContentProps}
            DialogActionsProps={DialogActionsProps}
            handleClose={handleClose}
            children={children}
            actions={actions}
            footer={footer}
            variant={variant}
            ConfirmButtonProps={ConfirmButtonProps}
            CancelButtonProps={CancelButtonProps}
          />
        </DialogRoot>
      </StopOverlayPropagation>
    );
  }
);

Dialog.propTypes = {
  DialogHeaderProps: PropTypes.shape({
    title: PropTypes.string.isRequired,
    /**
     * props available in [Tooltip](./index.html?path=/docs/components-feedback-tooltip--docs) component
     **/
    TooltipProps: PropTypes.object,
    /**
     * props available for [Badge](./index.html?path=/docs/components-data-display-badge--docs) component
     **/
    BadgeProps: PropTypes.object,
    description: PropTypes.node,
    disclaimer: PropTypes.string,
    /**
     * props available in Material's [DialogTitle](https://mui.com/material-ui/api/dialog-title/) component
     **/
    DialogTitleProps: PropTypes.object
  }),
  /**
   * props available in Material's [DialogContent](https://mui.com/material-ui/api/dialog-title/) component
   **/
  DialogContentProps: PropTypes.object,
  /**
   * props available in Material's [DialogActions](https://mui.com/material-ui/api/dialog-actions/) component
   **/
  DialogActionsProps: PropTypes.object,
  /** When passing message/alert you should also pass CancelButtonProps and ConfirmButtonProps*/
  variant: PropTypes.oneOf(['default', 'message', 'alert']),
  ConfirmButtonProps: PropTypes.shape({
    text: PropTypes.string,
    action: PropTypes.func
  }),
  CancelButtonProps: PropTypes.shape({
    text: PropTypes.string,
    action: PropTypes.func
  }),
  isOpen: PropTypes.bool.isRequired,
  /** (event:object, reason: string) => void */
  handleClose: PropTypes.func,
  children: PropTypes.node,
  fullContentWidth: PropTypes.bool,
  size: PropTypes.oneOf(['xs', 's', 'm', 'l', 'xl', 'fullWidth', 'fullScreen']),
  scroll: PropTypes.oneOf(['paper', 'body']),
  actions: PropTypes.node,
  footer: PropTypes.node
};

Dialog.classes = classes;
Dialog.displayName = 'Dialog';

export default Dialog;
